// ─── Auth Service Layer ───────────────────────────────────────────────────────
// Story 2: Implement Auth API Service
// All endpoints against /auth/

import api, { parseAPIError } from './api';
import {
  AuthResponse,
  ForgotPasswordPayload,
  LoginPayload,
  MessageResponse,
  RegisterPayload,
  ResendVerificationPayload,
  ResetPasswordPayload,
  VerifyEmailPayload,
} from '../types/auth.types';

// ─── Endpoints ────────────────────────────────────────────────────────────────

// authService.ts — fix these:
const AUTH = {
  LOGIN:                '/auth/login/',
  REGISTER:             '/auth/register/',
  LOGOUT:               '/auth/logout/',
  REFRESH:              '/auth/token/refresh/',
  FORGOT_PASSWORD:      '/auth/password/reset/',
  RESET_PASSWORD:       '/auth/password/reset/confirm/',
  VERIFY_EMAIL:         '/auth/email/verify/',        
  RESEND_VERIFICATION:  '/auth/email/resend/',     
} as const;

// ─── Service Methods ──────────────────────────────────────────────────────────

/**
 * Authenticate a user with email and password.
 * Returns access token + user data. Refresh token set via HTTP-only cookie.
 */
export async function login(payload: LoginPayload): Promise<AuthResponse> {
  try {
    const { data } = await api.post<AuthResponse>(AUTH.LOGIN, payload);
    return data;
  } catch (error) {
    throw parseAPIError(error);
  }
}

/**
 * Register a new customer account.
 * Triggers email verification flow on backend.
 */
export async function register(payload: RegisterPayload): Promise<MessageResponse> {
  try {
    const { data } = await api.post<MessageResponse>(AUTH.REGISTER, payload);
    return data;
  } catch (error) {
    throw parseAPIError(error);
  }
}

/**
 * Invalidate the current session.
 * Backend blacklists the refresh token JTI.
 */
export async function logout(): Promise<void> {
  try {
    await api.post(AUTH.LOGOUT);
  } catch (error) {
    // Best-effort: always clear local state even if request fails
    throw parseAPIError(error);
  }
}

/**
 * Exchange refresh token (HTTP-only cookie) for a new access token.
 * Called automatically by the Axios interceptor on 401.
 */
export async function refreshToken(): Promise<string> {
  try {
    const { data } = await api.post<{ status: string; data: { access: string } }>(AUTH.REFRESH);
    return data.data.access;  // ← was data.access
  } catch (error) {
    throw parseAPIError(error);
  }
}

/**
 * Initiate forgot-password flow.
 * Backend sends reset email. Response is identical for found/not-found (no enumeration).
 */
export async function forgotPassword(payload: ForgotPasswordPayload): Promise<MessageResponse> {
  try {
    const { data } = await api.post<MessageResponse>(AUTH.FORGOT_PASSWORD, payload);
    return data;
  } catch (error) {
    throw parseAPIError(error);
  }
}

/**
 * Submit new password with reset token from URL.
 */
export async function resetPassword(payload: ResetPasswordPayload): Promise<MessageResponse> {
  try {
    const { data } = await api.post<MessageResponse>(AUTH.RESET_PASSWORD, payload);
    return data;
  } catch (error) {
    throw parseAPIError(error);
  }
}

/**
 * Verify email address using token from verification email.
 */
export async function verifyEmail(payload: VerifyEmailPayload): Promise<MessageResponse> {
  try {
    const { data } = await api.post<MessageResponse>(AUTH.VERIFY_EMAIL, payload);
    return data;
  } catch (error) {
    throw parseAPIError(error);
  }
}

/**
 * Resend verification email to the given address.
 */
export async function resendVerification(
  payload: ResendVerificationPayload
): Promise<MessageResponse> {
  try {
    const { data } = await api.post<MessageResponse>(AUTH.RESEND_VERIFICATION, payload);
    return data;
  } catch (error) {
    throw parseAPIError(error);
  }
}

const authService = {
  login,
  register,
  logout,
  refreshToken,
  forgotPassword,
  resetPassword,
  verifyEmail,
  resendVerification,
};

export default authService;